import { useState, useEffect } from 'react';
import { useHeroData } from '../hooks/useHeroData';
import { useAboutData } from '../hooks/useAboutData';
import { useExperiences } from '../hooks/useExperiences';
import { useProjects } from '../hooks/useProjects';
import { useCertifications } from '../hooks/useCertifications';
import { fetchWithCache } from '../utils/apiCache';

/**
 * Mapeo de tecnologías a categorías
 * Añade aquí nuevas tecnologías según las vayas usando
 */
const TECH_CATEGORIES = {
  // Frontend
  'JavaScript': 'Frontend',
  'TypeScript': 'Frontend',
  'HTML': 'Frontend',
  'HTML5': 'Frontend',
  'CSS': 'Frontend',
  'CSS3': 'Frontend',
  'SCSS': 'Frontend',
  'Sass': 'Frontend',
  'jQuery': 'Frontend',

  // Frameworks Frontend
  'React': 'Frameworks Frontend',
  'Vue': 'Frameworks Frontend',
  'Vue.js': 'Frameworks Frontend',
  'Angular': 'Frameworks Frontend',
  'Next.js': 'Frameworks Frontend',
  'Nuxt.js': 'Frameworks Frontend',
  'Tailwind': 'Frameworks Frontend',
  'TailwindCSS': 'Frameworks Frontend',
  'Tailwind CSS': 'Frameworks Frontend',
  'Bootstrap': 'Frameworks Frontend',
  'Redux': 'Frameworks Frontend',
  'Vuex': 'Frameworks Frontend',
  'Pinia': 'Frameworks Frontend',
  'React Router': 'Frameworks Frontend',
  'React Toastify': 'Frameworks Frontend',
  'Framer Motion': 'Frameworks Frontend',

  // Backend
  'Python': 'Backend',
  'PHP': 'Backend',
  'Node.js': 'Backend',
  'Node': 'Backend',
  'Java': 'Backend',
  'C#': 'Backend',
  'Ruby': 'Backend',
  'Go': 'Backend',
  'Golang': 'Backend',

  // Frameworks Backend
  'Express': 'Frameworks Backend',
  'Express.js': 'Frameworks Backend',
  'Django': 'Frameworks Backend',
  'Flask': 'Frameworks Backend',
  'FastAPI': 'Frameworks Backend',
  'Laravel': 'Frameworks Backend',
  'Symfony': 'Frameworks Backend',
  'Spring': 'Frameworks Backend',
  'Spring Boot': 'Frameworks Backend',
  'Hibernate': 'Frameworks Backend',
  '.NET': 'Frameworks Backend',
  'Rails': 'Frameworks Backend',

  // Database
  'MySQL': 'Database',
  'PostgreSQL': 'Database',
  'MongoDB': 'Database',
  'SQLite': 'Database',
  'Redis': 'Database',
  'MariaDB': 'Database',
  'Oracle': 'Database',
  'SQL Server': 'Database',
  'Elasticsearch': 'Database',
  'Cassandra': 'Database',

  // APIs y Protocolos
  'REST': 'APIs',
  'SOAP': 'APIs',
  'GraphQL': 'APIs',
  'API REST': 'APIs',
  'Websockets': 'APIs',
  'SOA': 'APIs',

  // E-commerce
  'PrestaShop': 'E-commerce',
  'WooCommerce': 'E-commerce',
  'Shopify': 'E-commerce',
  'Magento': 'E-commerce',
  'BigCommerce': 'E-commerce',

  // DevOps
  'Docker': 'DevOps',
  'Kubernetes': 'DevOps',
  'AWS': 'DevOps',
  'Azure': 'DevOps',
  'Google Cloud': 'DevOps',
  'GCP': 'DevOps',
  'Git': 'DevOps',
  'GitHub': 'DevOps',
  'GitLab': 'DevOps',
  'Jenkins': 'DevOps',
  'CI/CD': 'DevOps',
  'Linux': 'DevOps',
  'Bash': 'DevOps',
  'Shell': 'DevOps',
  'Nginx': 'DevOps',
  'Apache': 'DevOps',
  'Terraform': 'DevOps',
  'Ansible': 'DevOps',

  // Mobile
  'React Native': 'Mobile',
  'Flutter': 'Mobile',
  'Ionic': 'Mobile',
  'Swift': 'Mobile',
  'Kotlin': 'Mobile',
  'Android': 'Mobile',
  'iOS': 'Mobile',

  // Testing
  'Jest': 'Testing',
  'Pytest': 'Testing',
  'PHPUnit': 'Testing',
  'Mocha': 'Testing',
  'Cypress': 'Testing',
  'Selenium': 'Testing',
  'JUnit': 'Testing',

  // IoT
  'MQTT': 'IoT',
  'Arduino': 'IoT',
  'Raspberry Pi': 'IoT',
  'IoT': 'IoT',

  // Herramientas y Servicios
  'Webpack': 'Herramientas',
  'Vite': 'Herramientas',
  'Babel': 'Herramientas',
  'ESLint': 'Herramientas',
  'Prettier': 'Herramientas',
  'Postman': 'Herramientas',
  'Insomnia': 'Herramientas',
  'VS Code': 'Herramientas',
  'EmailJS': 'Herramientas',
  'Scripting': 'Herramientas',
  'IBM Integration Bus': 'Herramientas',
  'WebSphere Message Broker': 'Herramientas',
  'Formación en TI': 'Herramientas',
  'Innovación en Ciencia': 'Herramientas',
};

/**
 * Hook para recopilar todos los datos necesarios para el CV
 * Centraliza la lógica de obtención de datos de todos los hooks existentes
 */
export const useCVData = () => {
  const [cvData, setCvData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contactInfo, setContactInfo] = useState(null);
  const [visibleSocialLinks, setVisibleSocialLinks] = useState([]);

  // Hooks existentes
  const { heroData, loading: heroLoading, error: heroError } = useHeroData();
  const { aboutData, loading: aboutLoading, error: aboutError } = useAboutData();
  const { experiences, loading: expLoading } = useExperiences();
  const { projectsData, loading: projLoading, error: projError } = useProjects();
  const { certifications, loading: certLoading } = useCertifications();

  useEffect(() => {
    // Obtener información de contacto y social links visibles
    const loadContactInfo = async () => {
      try {
        // Obtener todos los social links
        const url = `${process.env.REACT_APP_BACKEND_URL}/social-links`;
        const result = await fetchWithCache(url);
        
        if (result.status === 404 || !result.data) {
          setContactInfo({ phone: null, email: null, website: null });
          setVisibleSocialLinks([]);
          return;
        }

        const socialLinks = result.data;

        // Separar links de contacto de social links para CV
        const phoneLink = socialLinks.find(link => link.name === "Phone");
        const emailLink = socialLinks.find(link => link.name === "Email");

        // Links para mostrar en CV
        const visibleInCV = socialLinks
          .filter(link => link.show_in_cv && link.name !== "Descargar CV")
          .sort((a, b) => a.order - b.order);

        const info = {
          phone: phoneLink ? phoneLink.url.replace("tel:", "") : null,
          email: emailLink ? emailLink.url.replace("mailto:", "") : null,
        };

        /* console.log('📞 Contact info loaded:', info); */
        /* console.log('📄 Visible social links for CV:', visibleInCV); */
        
        setContactInfo(info);
        setVisibleSocialLinks(visibleInCV);
      } catch (error) {
        console.error('Error loading contact info:', error);
        setContactInfo({ phone: null, email: null, website: null });
        setVisibleSocialLinks([]);
      }
    };
    loadContactInfo();
  }, []);

  useEffect(() => {
    // Esperar a que todos los datos se carguen
    const allLoaded = !heroLoading && !aboutLoading && !expLoading && !projLoading && !certLoading && contactInfo !== null;

    if (allLoaded) {
      // Verificar si hay errores críticos
      const hasError = heroError || aboutError || projError;
      
      if (hasError) {
        setError('Error al cargar algunos datos del portfolio');
        setIsLoading(false);
        return;
      }

      // Verificar que tengamos al menos los datos básicos (hero)
      if (!heroData) {
        setError('No hay datos disponibles para generar el CV');
        setIsLoading(false);
        return;
      }

      // Función para extraer tecnologías únicas de experiencias y proyectos
      const extractSkills = () => {
        const techSet = new Set();

        // Extraer de experiencias
        experiences.forEach(company => {
          company.projects.forEach(project => {
            if (project.habilities && Array.isArray(project.habilities)) {
              project.habilities.forEach(tech => techSet.add(tech));
            }
          });
        });

        // Extraer de proyectos
        projectsData.forEach(project => {
          if (project.technologies && Array.isArray(project.technologies)) {
            project.technologies.forEach(tech => techSet.add(tech));
          }
        });

        // Convertir a array y agrupar por categoría
        const skillsArray = Array.from(techSet);
        const groupedSkills = {};

        skillsArray.forEach(tech => {
          const category = TECH_CATEGORIES[tech] || 'Otras';
          if (!groupedSkills[category]) {
            groupedSkills[category] = [];
          }
          groupedSkills[category].push({
            name: tech,
            category: category
          });
        });

        // Convertir a array plano y ordenar por categoría y nombre
        const skillsFinal = [];
        Object.keys(groupedSkills).sort().forEach(category => {
          groupedSkills[category]
            .sort((a, b) => a.name.localeCompare(b.name))
            .forEach(skill => skillsFinal.push(skill));
        });

        return skillsFinal;
      };

      // Compilar todos los datos
      const compiledData = {
        // Información personal (Hero + datos de contacto desde API)
        personal: {
          name: heroData.name || 'Nombre no disponible',
          title: heroData.title || 'Título no disponible',
          description: heroData.description || '',
          // Social links con show_in_cv=true
          socialLinks: visibleSocialLinks,
          // Datos de contacto individuales
          phone: contactInfo?.phone || null,
          email: contactInfo?.email || null,
        },

        // Sobre mí
        about: aboutData ? {
          description: aboutData.description || '',
          keywords: aboutData.keywords || [],
        } : null,

        // Experiencias laborales
        experiences: experiences.map(exp => ({
          company: exp.company,
          totalDuration: exp.totalDuration,
          hasMultipleWorkTypes: exp.hasMultipleWorkTypes,
          projects: exp.projects.map(project => ({
            position: project.position,
            workType: project.work_type,
            startDate: project.start_date,
            endDate: project.end_date,
            description: project.description,
            habilities: project.habilities || [],
          })),
        })) || [],

        // Proyectos
        projects: projectsData.map(project => ({
          title: project.title,
          description: project.description,
          technologies: project.technologies || [],
          repository: project.code_link || null,
          demo: project.demo_link || null,
          date: project.created_at,
        })) || [],

        // Skills - Generadas dinámicamente desde experiencias y proyectos
        skills: extractSkills(),

        // Certificaciones
        certifications: certifications.map(cert => ({
          title: cert.title,
          issuer: cert.issuer,
          issue_date: cert.issue_date,
          expiry_date: cert.expiry_date || null,
          credential_url: cert.credential_url || null,
          description: cert.description || null,
        })) || [],
      };

      /* console.log('✅ CV Data compiled successfully:', compiledData); */
      setCvData(compiledData);
      setIsLoading(false);
    }
  }, [
    heroData, heroLoading, heroError,
    aboutData, aboutLoading, aboutError,
    experiences, expLoading,
    projectsData, projLoading, projError,
    certifications, certLoading,
    contactInfo,
    visibleSocialLinks
  ]);

  return { cvData, isLoading, error };
};
